import React, { useEffect, useRef } from "react"
import Split from "react-split"
import { ConfigProvider, Drawer } from "antd"
import { Loader } from "shared/ui/Loader/Loader.component"
import { useDashboardLayoutStore } from "stores/dashboardLayoutStore"
import { useDashboardStore } from "features/Dashboard/runtime/dashboardStore"
import DataTree from "features/QueryConsole/components/DataTree.component"
import ActionRow from "./ActionRow/ActionRow.component"
import ViewerHeader from "./ViewerHeader/ViewerHeader.component"
import PageTabs from "./PageTabs/PageTabs.component"
import DashboardGrid from "./Grid/DashboardGrid.component"
import Inspector from "./Inspector/Inspector.component"
import FilterBar from "./FilterBar/FilterBar.component"
import PrepErrorAlert from "./PrepErrorAlert/PrepErrorAlert.component"
import { WORKSPACE_THEME } from "./workspaceTheme"

interface DashboardWorkspaceProps {
  onSave: () => void
  onExport: () => void
  onOpenFilters: () => void
  isSaving: boolean
  onRegenerateCodebook: () => void
  isCodebookLoading: boolean
  narrow: boolean
  /** More actions for Build mode's top row (the assistant's toggle). */
  headerActions?: React.ReactNode
}

/**
 * Session state over the canvas: a quiet note while starting, the reason
 * when a restart failed. A failed preparation has its own alert at the top
 * of the canvas (PrepErrorAlert).
 */
const SessionNote: React.FC<{ onRetry: () => void }> = ({ onRetry }) => {
  const status = useDashboardStore((s) => s.session.status)
  const error = useDashboardStore((s) => s.session.error)
  if (status === "starting") {
    return (
      <div className="dq-session-note" role="status">
        <Loader fontSize={12} />
        Preparing the data
      </div>
    )
  }
  if (status === "error") {
    return (
      <div className="dq-session-note" data-tone="error" role="alert">
        <span>{error ?? "The data could not be prepared."}</span>
        <button type="button" className="dq-session-note__action" onClick={onRetry}>
          Try again
        </button>
      </div>
    )
  }
  return null
}

/**
 * The dashboard surface. Build mode borrows the console's layout: action
 * row, variables rail, the page canvas, and the inspector for the selected
 * tile, split with `react-split` (Drawers under 1024 px). View mode is a slim
 * header over the canvas.
 */
const DashboardWorkspace: React.FC<DashboardWorkspaceProps> = ({
  onSave,
  onExport,
  onOpenFilters,
  isSaving,
  onRegenerateCodebook,
  isCodebookLoading,
  narrow,
  headerActions,
}) => {
  const mode = useDashboardStore((s) => s.mode)
  const codebook = useDashboardStore((s) => s.codebook)
  const selectedTileId = useDashboardStore((s) => s.selectedTileId)
  const dataTreeVisible = useDashboardLayoutStore((s) => s.dataTreeVisible)
  const inspectorVisible = useDashboardLayoutStore((s) => s.inspectorVisible)
  const isMaximized = useDashboardLayoutStore((s) => s.isMaximized)
  const treeSplitSizes = useDashboardLayoutStore((s) => s.treeSplitSizes)
  const inspectorSplitSizes = useDashboardLayoutStore((s) => s.inspectorSplitSizes)
  const toggleDataTree = useDashboardLayoutStore((s) => s.toggleDataTree)
  const toggleInspector = useDashboardLayoutStore((s) => s.toggleInspector)
  const setTreeSplitSizes = useDashboardLayoutStore((s) => s.setTreeSplitSizes)
  const setInspectorSplitSizes = useDashboardLayoutStore((s) => s.setInspectorSplitSizes)
  const setMaximized = useDashboardLayoutStore((s) => s.setMaximized)
  const rootRef = useRef<HTMLDivElement>(null)
  // Drawers render inside the workspace, so they get the --qc-* tokens and
  // the rail rules, and stay inside the full screen overlay.
  const drawerContainer = (): HTMLElement => rootRef.current ?? document.body

  // Esc leaves full screen. Attached only while maximised so Esc still
  // reaches modals and dropdowns otherwise.
  useEffect(() => {
    if (!isMaximized) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setMaximized(false)
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [isMaximized, setMaximized])

  const centre = (
    <div className="dq-centre">
      {mode === "build" && <PageTabs />}
      {mode === "build" && (
        <div className="dq-build-filters">
          <FilterBar />
        </div>
      )}
      <PrepErrorAlert />
      <div className="dq-grid-scroll">
        <DashboardGrid />
      </div>
      <SessionNote onRetry={onRegenerateCodebook} />
    </div>
  )
  const tree = (
    <aside className="dq-rail" aria-label="Variables">
      <DataTree
        editor={null}
        codebook={codebook ?? []}
        regenerateCodebook={onRegenerateCodebook}
        isCodebookLoading={isCodebookLoading}
      />
    </aside>
  )
  const showInspector =
    mode === "build" && inspectorVisible && selectedTileId !== null
  const showTree = mode === "build" && dataTreeVisible
  const inspector = (
    <aside className="dq-rail dq-rail--right" aria-label="Inspector">
      {showInspector && <Inspector />}
    </aside>
  )

  // On a wide screen both splits are always rendered and a hidden rail is
  // hidden by CSS (data-tree / data-inspector), so selecting a tile or
  // switching mode never remounts the canvas.
  const body = narrow ? (
    <>
      {centre}
      <Drawer
        open={showTree}
        getContainer={drawerContainer}
        rootClassName="dq-drawer"
        placement="left"
        onClose={toggleDataTree}
        title="Variables"
        width={320}
      >
        {tree}
      </Drawer>
      <Drawer
        open={showInspector}
        getContainer={drawerContainer}
        rootClassName="dq-drawer"
        placement="right"
        onClose={toggleInspector}
        title="Inspector"
        width={360}
      >
        {inspector}
      </Drawer>
    </>
  ) : (
    <Split
      className="dq-split"
      data-tree={showTree}
      direction="horizontal"
      gutterSize={6}
      sizes={treeSplitSizes}
      onDragEnd={setTreeSplitSizes}
      minSize={[180, 400]}
    >
      {tree}
      <Split
        className="dq-split"
        data-inspector={showInspector}
        direction="horizontal"
        gutterSize={6}
        sizes={inspectorSplitSizes}
        onDragEnd={setInspectorSplitSizes}
        minSize={[300, 260]}
      >
        {centre}
        {inspector}
      </Split>
    </Split>
  )

  return (
    <ConfigProvider theme={WORKSPACE_THEME}>
      <div
        ref={rootRef}
        className="dashboard-root dq-workspace"
        data-mode={mode}
        data-maximized={isMaximized || undefined}
      >
        {mode === "build" ? (
          <ActionRow
            onSave={onSave}
            onExport={onExport}
            onOpenFilters={onOpenFilters}
            isSaving={isSaving}
            extraActions={headerActions}
          />
        ) : (
          <ViewerHeader onExport={onExport} />
        )}
        {body}
      </div>
    </ConfigProvider>
  )
}

export default DashboardWorkspace
